import React from 'react';
import { t } from '@lingui/macro';
import { Button, ButtonProps, useDisclosure } from '@cardboard-ui/react';
import { graphql, useFragment } from 'utils/graphClient';
import { ReminderModal } from 'components/OptionsMenu/options/ReminderOption/ReminderModal';
import { ReminderButton_node$key } from './__generated__/ReminderButton_node.graphql';
import { ReminderIcon } from 'components/icons';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';

const nodeFragment = graphql`
  fragment ReminderButton_node on Node {
    __typename
    id
    ... on FolderNodeInterface {
      publishState
      canDelete {
        value
      }
    }
    ... on FolderItem {
      content {
        __typename
      }
    }
    ... on VaultItem {
      canDelete {
        value
      }
      content {
        __typename
      }
    }
    ... on Event {
      publishState
      canDelete {
        value
      }
    }
  }
`;

interface ReminderButtonProps extends ButtonProps {
  node: ReminderButton_node$key;
  closeMenu?: () => void;
}

export const ReminderButton = ({
  node: _node,
  closeMenu,
  children,
  ...props
}: ReminderButtonProps) => {
  const { __typename, id, publishState, canDelete, content } = useFragment(
    nodeFragment,
    _node,
  );
  const activeLayout = useActiveLayout();
  const isDesktop = activeLayout === Layout.DESKTOP;

  const {
    isOpen: isOpenOverlay,
    onOpen: onOpenOverlay,
    onClose: onCloseOverlay,
  } = useDisclosure();

  const isEvent = __typename === 'Event';
  const isVaultItem = __typename === 'VaultItem';
  const isForm = content?.__typename === 'Form';
  if (!(isEvent || isVaultItem || isForm)) return null;

  const isReadyForReminder =
    isEvent || isForm ? publishState === 'PUBLISHED' : isVaultItem;
  const canSendReminder = canDelete?.value && isReadyForReminder;
  if (!canSendReminder) return null;

  const onOpen = () => {
    onOpenOverlay();
    if (isDesktop) {
      closeMenu && closeMenu();
    }
  };
  const onClose = () => {
    onCloseOverlay();
    if (!isDesktop) {
      closeMenu && closeMenu();
    }
  };

  return (
    <>
      <Button
        w="100%"
        size="sm"
        rightIcon={<ReminderIcon />}
        justifyContent="space-between"
        onClick={() => {
          onOpen();
        }}
        {...props}
      >
        {t`Send reminder`}
      </Button>
      <ReminderModal isOpen={isOpenOverlay} onClose={onClose} nodeId={id} />
    </>
  );
};
